import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import * as echarts from 'echarts';
import type { RawDistribution } from '../utils/rawDistribution';
import InfoTooltip from '@/shared/components/forms/InfoTooltip';

interface Props {
  distribution: RawDistribution;
}

interface SingleChartProps {
  title: string;
  xLabel: string;
  yLabel: string;
  xData: string[];
  yData: number[];
}

const SingleDistributionChart: React.FC<SingleChartProps> = ({ title, xLabel, yLabel, xData, yData }) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);

  useEffect(() => {
    if (!chartRef.current) return;
    chartInstance.current = echarts.init(chartRef.current);
    const handleResize = () => chartInstance.current?.resize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      chartInstance.current?.dispose();
      chartInstance.current = null;
    };
  }, []);

  useEffect(() => {
    if (!chartInstance.current) return;
    chartInstance.current.setOption(
      {
        title: { text: title, left: 'center', textStyle: { fontSize: 14 } },
        tooltip: { trigger: 'axis' },
        grid: { left: 70, right: 20, top: 50, bottom: 50 },
        xAxis: {
          type: 'category',
          name: xLabel,
          nameGap: 30,
          nameLocation: 'middle',
          data: xData,
        },
        yAxis: {
          type: 'log',
          name: yLabel,
          nameGap: 48,
          nameLocation: 'middle',
          // Compact large frequencies (1,000,000 -> "1M") so wide labels don't
          // overflow the grid and overlap the neighbouring chart (#108).
          axisLabel: {
            formatter: (val: number) =>
              val >= 1e6 ? `${val / 1e6}M` : val >= 1e3 ? `${val / 1e3}k` : `${val}`,
          },
        },
        series: [{ type: 'bar', data: yData }],
      },
      true,
    );
  }, [title, xLabel, yLabel, xData, yData]);

  return <div ref={chartRef} style={{ width: '100%', height: 360 }} />;
};

const RawDataDistribution: React.FC<Props> = ({ distribution }) => {
  const { t } = useTranslation('firstProcessing');
  const { t: tDataUpload } = useTranslation('dataUpload');

  if (distribution.deltaDistribution.length === 0) return null;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div>
        <div className="flex items-center mb-1">
          <h4 className="text-sm font-medium text-foreground">{t('rawDistribution.stepTitle')}</h4>
          <InfoTooltip text={tDataUpload('tooltips.distributionSteps')} />
        </div>
        <SingleDistributionChart
          title=""
          xLabel={t('rawDistribution.stepX')}
          yLabel={t('rawDistribution.yLog')}
          xData={distribution.deltaDistribution.map((d) => d.deltaMin.toString())}
          yData={distribution.deltaDistribution.map((d) => d.count)}
        />
      </div>
      <div>
        <div className="flex items-center mb-1">
          <h4 className="text-sm font-medium text-foreground">{t('rawDistribution.offsetTitle')}</h4>
          <InfoTooltip text={tDataUpload('tooltips.distributionOffset')} />
        </div>
        <SingleDistributionChart
          title=""
          xLabel={t('rawDistribution.offsetX')}
          yLabel={t('rawDistribution.yLog')}
          xData={distribution.offsetDistribution.map((d) => d.offsetMin.toString())}
          yData={distribution.offsetDistribution.map((d) => d.count)}
        />
      </div>
    </div>
  );
};

export default RawDataDistribution;
